import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useInstallPrompt } from '../../../../contexts/InstallPromptContext';
import useLocalization from '../../../../hooks/useLocalization';
import { useSidebarPresentation } from '../contexts/SidebarPresentationContext';
import NeobetCompactSidebarButton from './NeobetCompactSidebarButton';

const styles = {
  section: {
    width: 1,
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: 'neutral.600',
  },
  sectionHeaderButton: {
    display: 'flex',
    alignItems: 'center',
    width: 1,
    minHeight: 40,
    justifyContent: 'flex-start',
    gap: 1,
    px: 1.5,
    py: 1.125,
    color: 'text.primary',
    textAlign: 'left',
    backgroundColor: 'neutral.600',
    '&:hover': {
      backgroundColor: 'neutral.500',
    },
  },
  sectionHeaderIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    flexShrink: 0,
    color: 'text.primary',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  sectionHeaderTitle: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '20px',
  },
} as const;

type NeobetInstallAppButtonProps = {
  isCompact?: boolean;
};

const NeobetInstallAppButton = ({ isCompact = false }: NeobetInstallAppButtonProps) => {
  const { canShowInstallPrompt, showInstallPrompt } = useInstallPrompt();
  const { closeDrawer } = useSidebarPresentation();
  const { t } = useLocalization();

  const label = t('installAsApp', 'Install as App');

  const handleClick = () => {
    showInstallPrompt();
    closeDrawer();
  };

  if (!canShowInstallPrompt) {
    return null;
  }

  if (isCompact) {
    return (
      <NeobetCompactSidebarButton onClick={handleClick} aria-label={label} testId='neobet-sidebar-install-app'>
        <DownloadRoundedIcon fontSize='small' />
      </NeobetCompactSidebarButton>
    );
  }

  return (
    <Box sx={styles.section} data-testid='neobet-sidebar-install-app'>
      <ButtonBase onClick={handleClick} sx={styles.sectionHeaderButton} aria-label={label}>
        <Box sx={styles.sectionHeaderIcon}>
          <DownloadRoundedIcon fontSize='small' />
        </Box>
        <Typography variant='h5' sx={styles.sectionHeaderTitle}>
          {label}
        </Typography>
      </ButtonBase>
    </Box>
  );
};

export default NeobetInstallAppButton;
